/**
 * Your hand, on a rack along the front edge of the table.
 *
 * This is the one place a beginner acts, so it gets the largest tiles, real
 * depth, and states that never rely on colour alone: a legal tile lifts on
 * hover, an illegal one is dimmed *and* disabled, the coach's suggestion is
 * outlined *and* announced in its label, and a freshly drawn tile is both
 * separated from the rest and captioned.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, PointerEvent as ReactPointerEvent } from "react";
import type { Tile, TileKind } from "@kikik/mahjong-core";
import { useI18n } from "../i18n";
import type { TableModel } from "../game/tableModel";
import { discardableIds } from "../game/tableModel";
import {
  arrangeRack,
  canonicalRack,
  moveRackTile,
  persistRackPreferences,
  readAutoSort,
  readManualOrder,
  reconcileRackOrder,
} from "../game/rackOrder";
import { MeldRow } from "./MeldRow";
import { MahjongTile } from "./tiles";
import { playCue } from "../game/tableAudio";

export interface PlayerHandPanelProps {
  model: TableModel;
  hintTileId: number | null;
  waits: readonly TileKind[];
  onDiscard: (tileId: number) => void;
}

export function PlayerHandPanel({ model, hintTileId, waits, onDiscard }: PlayerHandPanelProps) {
  const { t, tile, tiles } = useI18n();
  const legal = discardableIds(model);
  const you = model.seats.find((seat) => seat.isYou);
  const canDiscard = legal.size > 0;
  const melds = you?.melds ?? [];
  const allTiles = useMemo(
    () => (model.drawnTile ? [...model.hand, model.drawnTile] : [...model.hand]),
    [model.hand, model.drawnTile],
  );
  const [autoSort, setAutoSort] = useState(readAutoSort);
  const [manualOrder, setManualOrder] = useState(readManualOrder);
  const drag = useRef<{ id: number; x: number; y: number; moved: boolean } | null>(null);
  const suppressClick = useRef(false);
  /*
   * Which tile is under the finger, as *state* rather than as the ref above:
   * the ref is what the drag logic reads on every pointer move, and this is the
   * one thing about the drag the rack has to repaint - the tile coming up off
   * the felt. Splitting them keeps a pointermove from re-rendering the hand.
   */
  const [dragging, setDragging] = useState<number | null>(null);

  useEffect(() => {
    setManualOrder((previous) => {
      const next = reconcileRackOrder(previous, allTiles);
      persistRackPreferences(autoSort, next);
      return next;
    });
  }, [allTiles, autoSort]);

  const displayed = autoSort ? canonicalRack(allTiles) : arrangeRack(allTiles, manualOrder);

  const updateOrder = (next: number[]): void => {
    setManualOrder(next);
    persistRackPreferences(false, next);
  };

  const move = (source: number, target: number): void => {
    const base = reconcileRackOrder(manualOrder, allTiles);
    updateOrder(moveRackTile(base, source, target));
  };

  const tileAtPoint = (event: ReactPointerEvent<HTMLDivElement>): number | null => {
    const element = globalThis.document?.elementFromPoint?.(event.clientX, event.clientY);
    const slot = element?.closest<HTMLElement>("[data-rack-tile-id]");
    const value = Number(slot?.dataset.rackTileId);
    return Number.isInteger(value) ? value : null;
  };

  const pointerMove = (event: ReactPointerEvent<HTMLDivElement>): void => {
    const active = drag.current;
    if (!active || autoSort) return;
    if (Math.hypot(event.clientX - active.x, event.clientY - active.y) > 6 && !active.moved) {
      active.moved = true;
      setDragging(active.id);
      playCue("lift");
    }
    if (!active.moved) return;
    const target = tileAtPoint(event);
    if (target !== null && target !== active.id) {
      move(active.id, target);
    }
  };

  const finishPointer = (): void => {
    if (drag.current?.moved) {
      suppressClick.current = true;
      globalThis.setTimeout(() => {
        suppressClick.current = false;
      }, 0);
      playCue("click");
    }
    drag.current = null;
    setDragging(null);
  };

  /*
   * Arranging a hand without a pointer.
   *
   * Dragging is the natural way to do this and it is not the only way anyone
   * can do it: a keyboard user, or anyone using a switch or head pointer, has
   * no drag at all. Alt with the arrow keys moves the focused tile one place,
   * which works on the tile button that already has focus for discarding, so
   * no extra tab stop appears in the rack. Plain arrow keys are left alone
   * because they are how the browser moves between the buttons.
   */
  const arrangeByKey = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (autoSort || !event.altKey) return;
    const step = event.key === "ArrowLeft" ? -1 : event.key === "ArrowRight" ? 1 : 0;
    if (step === 0) return;
    const slot = (event.target as HTMLElement | null)?.closest<HTMLElement>("[data-rack-tile-id]");
    const id = Number(slot?.dataset.rackTileId);
    if (!Number.isInteger(id)) return;

    const order = displayed.map((item) => item.id);
    const from = order.indexOf(id);
    const to = from + step;
    if (from < 0 || to < 0 || to >= order.length) return;

    event.preventDefault();
    const neighbour = order[to];
    if (neighbour === undefined) return;
    move(id, neighbour);
    playCue("click");
    // The tiles move under the focus ring, so put it back on the tile that was
    // being moved rather than leaving it on whatever now sits in that slot.
    globalThis.requestAnimationFrame?.(() => {
      const moved = globalThis.document?.querySelector<HTMLElement>(
        `[data-rack-tile-id="${id}"] button`,
      );
      moved?.focus();
    });
  };

  const renderTile = (item: Tile, index: number) => {
    const fresh = model.drawnTile?.id === item.id;
    return (
      <span
        key={item.id}
        className={[
          "hand__tile-slot",
          fresh ? "hand__tile-slot--fresh" : "",
          dragging === item.id ? "hand__tile-slot--dragging" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        data-rack-tile-id={item.id}
        style={{ "--slot": index } as CSSProperties}
        {...(fresh ? { "data-testid": "drawn-tile" } : {})}
        draggable={!autoSort}
        onDragStart={(event) => {
          event.dataTransfer.setData("text/plain", String(item.id));
          event.dataTransfer.effectAllowed = "move";
        }}
        onDragOver={(event) => {
          if (!autoSort) event.preventDefault();
        }}
        onDrop={(event) => {
          event.preventDefault();
          const source = Number(event.dataTransfer.getData("text/plain"));
          if (Number.isInteger(source)) move(source, item.id);
        }}
        onPointerDown={(event) => {
          if (!autoSort) {
            drag.current = { id: item.id, x: event.clientX, y: event.clientY, moved: false };
            event.currentTarget.setPointerCapture?.(event.pointerId);
          }
        }}
      >
        {fresh ? <span className="hand__drawn-label">{t("hand.drawn")}</span> : null}
        <MahjongTile
          kind={item.kind}
          size="md"
          fresh={fresh}
          hinted={hintTileId === item.id}
          disabled={!legal.has(item.id)}
          onClick={canDiscard ? () => {
            if (!suppressClick.current) onDiscard(item.id);
          } : undefined}
          label={t("hand.discardTile", { tile: tile(item.kind) })}
        />
      </span>
    );
  };

  return (
    <section className="hand" data-testid="hand-rack" aria-label={t("hand.rack")}>
      {/*
        * The count used to be a heading in small caps over the tiles - one more
        * label on a screen that already had too many. The tiles say how many
        * there are; a screen reader still gets the number, and gets it as the
        * heading of this region, which is what it is for.
        */}
      <h2 className="hand__title visually-hidden">
        {t("hand.title", { count: model.hand.length + (model.drawnTile ? 1 : 0) })}
      </h2>

      <div className="hand__rack">
        <div
          className={`hand__tiles ${autoSort ? "" : "hand__tiles--manual"}`}
          onPointerMove={pointerMove}
          onPointerUp={finishPointer}
          onPointerCancel={finishPointer}
          onKeyDown={arrangeByKey}
        >
          {displayed.map((item, index) => renderTile(item, index))}
        </div>

        {melds.length > 0 ? (
          <div className="hand__melds">
            <span className="hand__melds-label">{t("seat.melds")}</span>
            <MeldRow melds={melds} size="sm" />
          </div>
        ) : null}
      </div>

      {/*
        * Arranging the hand sits at the end of the rack, where a real player's
        * hand ends, rather than in a control strip above the tiles.
        */}
      <div className="hand__arrange" aria-label={t("hand.arrange")}>
        {waits.length > 0 ? (
          <p className="hand__ready">{t("hand.ready", { tiles: tiles(waits) })}</p>
        ) : null}
        <label className="hand__auto-sort">
          <input
            type="checkbox"
            checked={autoSort}
            onChange={(event) => {
              const enabled = event.target.checked;
              const current = displayed.map((item) => item.id);
              setAutoSort(enabled);
              setManualOrder(current);
              persistRackPreferences(enabled, current);
            }}
          />
          <span>{t("hand.autoSort")}</span>
        </label>
        <button
          type="button"
          className="button button--ghost hand__sort"
          onClick={() => {
            const sorted = canonicalRack(allTiles).map((item) => item.id);
            setManualOrder(sorted);
            persistRackPreferences(autoSort, sorted);
          }}
        >
          {t("hand.sort")}
        </button>
        {/* Said out loud, because a drag handle is invisible to anyone who
            cannot drag, and this is the only way they can arrange a hand. */}
        {autoSort ? null : <span className="hand__arrange-hint">{t("hand.moveHint")}</span>}
      </div>
    </section>
  );
}
